import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Plus,
  Edit,
  Trash2,
  Users,
  FileText,
  Calendar,
  Search,
  Filter,
  MoreVertical,
  UserPlus,
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import { useToast } from '../../hooks/useToast';
import ToastContainer from '../../components/ui/ToastContainer';
import { apiService } from '../../services/api';
import { settingsService } from '../../services/api/settings';
import { useLocation } from 'react-router-dom';

interface ProjectFormData {
  name: string;
  description: string;
  color: string;
  members: number[];
}

interface TeamFormData {
  name: string;
  description: string;
  members: number[];
}

/**
 * Met un projet reçu de l'API à la forme qu'attend l'écran.
 *
 * La conversion était recopiée à quatre endroits et la création s'en passait,
 * mais la cause première est côté serveur : le serializer de création ne
 * listait pas l'identifiant, ni la date, ni les compteurs. La réponse d'une
 * création décrivait donc la saisie, pas le projet. L'écran plaçait dans sa
 * liste un projet qu'il ne savait pas afficher, et il fallait recharger la
 * page pour le voir — le « plusieurs rafraîchissements nécessaires » rapporté
 * par les équipes du client.
 *
 * Rien n'est lu sans précaution ici, même une fois le serveur corrigé : une
 * levée pendant le rendu vide la page entière. Une carte incomplète se voit et
 * se corrige ; une page blanche laisse croire que la plateforme est tombée.
 */
const formaterProjet = (proj: any) => ({
  id: proj?.id != null ? String(proj.id) : '',
  name: proj?.name || 'Projet sans nom',
  description: proj?.description || '',
  color: proj?.color || '#3B82F6',
  status: proj?.status || 'active',
  priority: proj?.priority || 'medium',
  team_name: proj?.team_name || null,
  created_by_name: proj?.created_by_name || 'Inconnu',
  createdAt: proj?.created_at,
  updatedAt: proj?.updated_at,
  members: proj?.members || [],
  members_count: proj?.members_count || 0,
  documents_count: proj?.documents_count || 0,
  is_overdue: proj?.is_overdue || false,
  is_archived: proj?.is_archived || false,
});

const ProjectsPage: React.FC = () => {
  const location = useLocation();
  const { toasts, showSuccess, showError, removeToast } = useToast();
  const [activeTab, setActiveTab] = useState<'projects' | 'teams'>('projects');
  const [projects, setProjects] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [teamsInitialized, setTeamsInitialized] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teams, setTeams] = useState<any[]>([]);
  const [showNewTeamModal, setShowNewTeamModal] = useState(false);
  const [showEditTeamModal, setShowEditTeamModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState<any>(null);
  const [teamSearchQuery, setTeamSearchQuery] = useState('');

  // Charger les projets et utilisateurs depuis l'API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        console.log('📁 Chargement des projets et utilisateurs depuis le backend...');
        
        // Charger en parallèle
        const [projectsData, usersData] = await Promise.all([
          apiService.getProjects(),
          settingsService.getAllUsers()
        ]);
        
        console.log('✅ Projets chargés:', projectsData.length);
        console.log('✅ Utilisateurs chargés:', usersData.length);
        
        // Formater les projets pour l'UI
        const formattedProjects = projectsData.map(formaterProjet);

        // Formater les utilisateurs pour l'UI
        const formattedUsers = usersData.map((user: any) => ({
          id: user.id.toString(),
          name: user.full_name || `${user.first_name} ${user.last_name}`,
          email: user.email,
          role: user.role || 'user',
          department: user.department_name || 'Non assigné'
        }));
        
        setProjects(formattedProjects);
        setUsers(formattedUsers);
      } catch (err) {
        console.error('Erreur lors du chargement:', err);
        showError('Erreur de chargement', 'Impossible de charger les données');
        
        // Fallback vide
        setProjects([]);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [showError]);

  useEffect(() => {
    // Initialiser l'onglet basé sur l'URL au chargement de la page
    if (location.pathname === '/workspace/teams') {
      setActiveTab('teams');
      if (!teamsInitialized) {
        loadTeamsData();
      }
    } else if (location.pathname === '/workspace/projects') {
      setActiveTab('projects');
    }
  }, [location.pathname, teamsInitialized]);
  
  // Charger les données des équipes quand l'onglet est activé
  useEffect(() => {
    if (activeTab === 'teams' && !teamsInitialized) {
      loadTeamsData();
    }
  }, [activeTab, teamsInitialized]);

  // Filtrage des projets
  const filteredProjects = useMemo(() => {
    if (!projects || !Array.isArray(projects)) {
      return [];
    }
    return projects.filter(project => {
      const matchesQuery = !searchQuery || 
        project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (project.description && project.description.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesQuery;
    });
  }, [searchQuery, projects]);

  const handleNewProject = async (data: ProjectFormData) => {
    try {
      const newProject = await apiService.createProject(data);
      if (newProject?.id != null) {
        setProjects(prev => [formaterProjet(newProject), ...prev]);
      } else {
        // Réponse incomplète : on relit la liste plutôt que d'y placer un
        // projet amputé. C'est la même parade qu'à la modification, qui
        // l'appliquait déjà.
        const projectsData = await apiService.getProjects();
        setProjects(projectsData.map(formaterProjet));
      }
      setShowNewProjectModal(false);
      showSuccess('Projet créé avec succès', `${data.name} a été ajouté à vos projets`);
    } catch (err) {
      showError('Erreur lors de la création', 'Impossible de créer le projet. Veuillez réessayer.');
    }
  };

  const handleEditProject = async (data: ProjectFormData) => {
    if (!editingProject || !editingProject.id) {
      console.error('❌ editingProject invalide:', editingProject);
      showError('Erreur', 'Projet invalide');
      return;
    }

    try {
      console.log('✏️ Modification du projet:', editingProject.id, data);
      const updatedProject = await apiService.updateProject(editingProject.id, data);
      
      console.log('✅ Réponse API updateProject:', updatedProject);
      
      // Si la réponse est vide ou invalide, recharger la liste complète
      if (!updatedProject || !updatedProject.id) {
        console.warn('⚠️ Réponse API incomplète, rechargement de la liste des projets...');
        // Recharger tous les projets pour avoir les données à jour
        const projectsData = await apiService.getProjects();
        setProjects(projectsData.map(formaterProjet));
      } else {
        // Reformater pour l'UI, en retombant sur la saisie quand l'API est
        // avare : elle ne renvoie pas toujours tous les champs à la mise à jour.
        const formattedProject = {
          ...formaterProjet(updatedProject),
          name: updatedProject.name || data.name,
          description: updatedProject.description || data.description || '',
          color: updatedProject.color || data.color || '#3B82F6',
          members: updatedProject.members || data.members || [],
        };

        // Mettre à jour le projet dans la liste
        setProjects(prev => prev.map(p => String(p.id) === String(editingProject.id) ? formattedProject : p));
      }
      
      setShowEditProjectModal(false);
      setEditingProject(null);
      showSuccess('Projet mis à jour', `${data.name} a été modifié avec succès`);
    } catch (err: any) {
      console.error('❌ Erreur lors de la modification du projet:', err);
      console.error('❌ Détails de l\'erreur:', err.message, err.response);
      
      // Même en cas d'erreur, si la requête a réussi (200 OK), recharger la liste
      // pour être sûr d'avoir les données à jour
      try {
        console.log('🔄 Rechargement de la liste des projets après erreur...');
        const projectsData = await apiService.getProjects();
        setProjects(projectsData.map(formaterProjet));
        setShowEditProjectModal(false);
        setEditingProject(null);
        showSuccess('Projet mis à jour', `${data.name} a été modifié avec succès`);
      } catch (reloadError) {
        console.error('❌ Erreur lors du rechargement:', reloadError);
        showError('Erreur lors de la modification', 'Impossible de modifier le projet. Veuillez réessayer.');
      }
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le projet "${project?.name}" ?`)) {
      try {
        await apiService.deleteProject(projectId);
        setProjects(prev => prev.filter(p => p.id !== projectId));
        showSuccess('Projet supprimé', `${project?.name} a été supprimé de vos projets`);
      } catch (err) {
        showError('Erreur lors de la suppression', 'Impossible de supprimer le projet. Veuillez réessayer.');
      }
    }
  };

  const loadTeamsData = async () => {
    try {
      setTeamsLoading(true);
      const teamsData = await apiService.getTeams();

      const formattedTeams = teamsData.map((team: any) => ({
        id: team.id.toString(),
        name: team.name,
        description: team.description || '',
        members: team.members || [],
        members_count: team.members_count || 0,
        created_by_name: team.created_by_name || 'Inconnu',
        createdAt: team.created_at,
        is_active: team.is_active !== false,
      }));

      setTeams(formattedTeams);
      setTeamsInitialized(true);
    } catch (err) {
      console.error('Erreur lors du chargement des équipes:', err);
      showError('Erreur de chargement', 'Impossible de charger les équipes');
      setTeams([]);
    } finally {
      setTeamsLoading(false);
    }
  };

  const filteredTeams = useMemo(() => {
    return teams.filter(team => {
      const query = teamSearchQuery.toLowerCase();
      return (
        !teamSearchQuery ||
        team.name.toLowerCase().includes(query) ||
        team.description.toLowerCase().includes(query)
      );
    });
  }, [teams, teamSearchQuery]);

  const getTeamMembers = (memberIds: number[]): any[] => {
    if (!memberIds || !Array.isArray(memberIds)) return [];
    return users.filter((user: any) => memberIds.includes(parseInt(user.id)));
  };

  const getTeamDocuments = (_teamId: string): any[] => [];
  const getTeamProjects = (_teamId: string): any[] => [];

  const handleNewTeam = async (data: TeamFormData) => {
    try {
      const newTeam = await apiService.createTeam(data);
      const formattedTeam = {
        id: newTeam.id.toString(),
        name: newTeam.name,
        description: newTeam.description || '',
        members: newTeam.members || [],
        members_count: newTeam.members_count || 0,
        created_by_name: newTeam.created_by_name || 'Inconnu',
        createdAt: newTeam.created_at,
        is_active: newTeam.is_active !== false,
      };

      setTeams(prev => [formattedTeam, ...prev]);
      setShowNewTeamModal(false);
      showSuccess('Équipe créée', `${data.name} a été ajoutée à vos équipes`);
    } catch (err) {
      console.error('❌ Erreur création équipe:', err);
      showError('Erreur lors de la création', 'Impossible de créer l\'équipe. Veuillez réessayer.');
    }
  };

  const handleEditTeam = async (data: TeamFormData) => {
    if (!editingTeam || !editingTeam.id) {
      showError('Erreur', 'Équipe invalide');
      return;
    }

    try {
      const updatedTeam = await apiService.updateTeam(editingTeam.id, data);
      const formattedTeam = {
        id: updatedTeam.id.toString(),
        name: updatedTeam.name,
        description: updatedTeam.description || '',
        members: updatedTeam.members || [],
        members_count: updatedTeam.members_count || 0,
        created_by_name: updatedTeam.created_by_name || 'Inconnu',
        createdAt: updatedTeam.created_at,
        is_active: updatedTeam.is_active !== false,
      };

      setTeams(prev => prev.map(team => (team.id === editingTeam.id ? formattedTeam : team)));
      setShowEditTeamModal(false);
      setEditingTeam(null);
      showSuccess('Équipe mise à jour', `${data.name} a été modifiée avec succès`);
    } catch (err) {
      console.error('❌ Erreur modification équipe:', err);
      showError('Erreur lors de la modification', 'Impossible de modifier l\'équipe. Veuillez réessayer.');
    }
  };

  const handleDeleteTeam = async (teamId: string) => {
    const team = teams.find(t => t.id === teamId);
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer l'équipe "${team?.name}" ?`)) {
      try {
        await apiService.deleteTeam(teamId);
        setTeams(prev => prev.filter(t => t.id !== teamId));
        showSuccess('Équipe supprimée', `${team?.name} a été supprimée`);
      } catch (err) {
        console.error('❌ Erreur suppression équipe:', err);
        showError('Erreur lors de la suppression', 'Impossible de supprimer l\'équipe');
      }
    }
  };

  const getProjectDocuments = (projectId: string) => {
    // Les documents sont chargés depuis le backend via documents_count
    // Pour afficher la liste complète, il faudrait appeler projectService.getProjectDocuments()
    return [];
  };

  const getProjectMembers = (memberIds: any[]) => {
    // Les membres sont maintenant des IDs numériques du backend
    // Retourner une liste vide car on affiche members_count
    return [];
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="bg-eg-blanc rounded-md shadow border border-eg-bordure overflow-hidden">
          <div className="flex">
            <button
              onClick={() => {
                setActiveTab('projects');
              }}
              className={`flex-1 px-6 py-4 text-left text-sm font-medium transition-colors ${
                activeTab === 'projects'
                  ? 'bg-eg-papier text-eg-brun border-b-2 border-eg-accent'
                  : 'text-eg-gris hover:bg-eg-papier'
              }`}
            >
              Projets
            </button>
            <button
              onClick={() => {
                setActiveTab('teams');
                if (!teamsInitialized) {
                  loadTeamsData();
                }
              }}
              className={`flex-1 px-6 py-4 text-left text-sm font-medium transition-colors ${
                activeTab === 'teams'
                  ? 'bg-eg-papier text-eg-brun border-b-2 border-eg-accent'
                  : 'text-eg-gris hover:bg-eg-papier'
              }`}
            >
              Équipes
            </button>
          </div>
        </div>

        {activeTab === 'projects' ? (
          <>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-eg-encre">Projets</h1>
                <p className="text-eg-gris">
                  {filteredProjects?.length || 0} projet{(filteredProjects?.length || 0) !== 1 ? 's' : ''} trouvé{(filteredProjects?.length || 0) !== 1 ? 's' : ''}
                </p>
              </div>
              <Button
                onClick={() => setShowNewProjectModal(true)}
                icon={<Plus className="w-4 h-4" />}
              >
                Nouveau Projet
              </Button>
            </div>

            <Card>
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1">
                  <Input
                    placeholder="Rechercher par nom, description..."
                    value={searchQuery}
                    onChange={setSearchQuery}
                    icon={<Search className="w-5 h-5" />}
                  />
                </div>
                <Button
                  variant={showFilters ? 'primary' : 'outline'}
                  onClick={() => setShowFilters(!showFilters)}
                  icon={<Filter className="w-4 h-4" />}
                >
                  Filtres
                </Button>
              </div>
            </Card>

            {loading && (
              <Card>
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                  <span className="ml-3 text-eg-gris">Chargement des projets...</span>
                </div>
              </Card>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProjects?.map((project) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="h-full">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: project.color }}
                        ></div>
                        <h3 className="text-lg font-semibold text-eg-encre">{project.name}</h3>
                      </div>
                      <div className="relative">
                        <button
                          onClick={() => {
                            setEditingProject(project);
                            setShowEditProjectModal(true);
                          }}
                          className="p-1 text-eg-estompe hover:text-eg-gris hover:bg-eg-voile rounded"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <p className="text-eg-gris text-sm mb-4 line-clamp-2">
                      {project.description || 'Aucune description'}
                    </p>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm text-eg-gris">
                        <div className="flex items-center">
                          <FileText className="w-4 h-4 mr-1" />
                          {project.documents_count || 0} documents
                        </div>
                        <div className="flex items-center">
                          <Users className="w-4 h-4 mr-1" />
                          {project.members_count || 0} membres
                        </div>
                      </div>

                      <div className="space-y-1">
                        {project.team_name && (
                          <div className="text-xs text-eg-gris">
                            Équipe: {project.team_name}
                          </div>
                        )}
                        {project.status && (
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              project.status === 'completed' ? 'bg-eg-valide/10 text-eg-valide' :
                              project.status === 'active' ? 'bg-eg-voile text-eg-brun' :
                              project.status === 'planning' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-eg-voile text-eg-encre'
                            }`}>
                              {project.status === 'completed' ? 'Terminé' :
                               project.status === 'active' ? 'Actif' :
                               project.status === 'planning' ? 'Planification' :
                               project.status}
                            </span>
                            {project.is_overdue && (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-eg-accent/10 text-eg-accent">
                                En retard
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-between text-xs text-eg-estompe">
                        <div className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {project.createdAt ? new Date(project.createdAt).toLocaleDateString('fr-FR') : 'Date inconnue'}
                        </div>
                        <div>
                          Par {project.created_by_name}
                        </div>
                      </div>

                      <div className="flex space-x-2 pt-3 border-t border-eg-bordure">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingProject(project);
                            setShowEditProjectModal(true);
                          }}
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Modifier
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteProject(project.id)}
                          className="text-eg-accent hover:text-red-700"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Supprimer
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>

            {filteredProjects.length === 0 && (
              <Card className="text-center py-12">
                <div className="w-12 h-12 bg-eg-voile rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-6 h-6 text-eg-estompe" />
                </div>
                <h3 className="text-lg font-medium text-eg-encre mb-2">Aucun projet trouvé</h3>
                <p className="text-eg-gris mb-4">Commencez par créer votre premier projet</p>
                <Button onClick={() => setShowNewProjectModal(true)}>
                  Créer un projet
                </Button>
              </Card>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-eg-encre">Équipes</h1>
                <p className="text-eg-gris">
                  {filteredTeams.length} équipe{filteredTeams.length !== 1 ? 's' : ''} trouvée{filteredTeams.length !== 1 ? 's' : ''}
                </p>
              </div>
              <Button
                onClick={() => setShowNewTeamModal(true)}
                icon={<Plus className="w-4 h-4" />}
              >
                Nouvelle Équipe
              </Button>
            </div>

            <Card>
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1">
                  <Input
                    placeholder="Rechercher par nom, description..."
                    value={teamSearchQuery}
                    onChange={setTeamSearchQuery}
                    icon={<Search className="w-5 h-5" />}
                  />
                </div>
              </div>
            </Card>

            {teamsLoading && (
              <Card>
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                  <span className="ml-3 text-eg-gris">Chargement des équipes...</span>
                </div>
              </Card>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTeams.map((team) => (
                <motion.div
                  key={team.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="h-full">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-eg-voile rounded-md flex items-center justify-center">
                          <Users className="w-5 h-5 text-eg-accent" />
                        </div>
                        <h3 className="text-lg font-semibold text-eg-encre">{team.name}</h3>
                      </div>
                      <div className="relative">
                        <button
                          onClick={() => {
                            setEditingTeam(team);
                            setShowEditTeamModal(true);
                          }}
                          className="p-1 text-eg-estompe hover:text-eg-gris hover:bg-eg-voile rounded"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <p className="text-eg-gris text-sm mb-4 line-clamp-2">
                      {team.description}
                    </p>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm text-eg-gris">
                        <div className="flex items-center">
                          <FileText className="w-4 h-4 mr-1" />
                          {getTeamDocuments(team.id).length} documents
                        </div>
                        <div className="flex items-center">
                          <Users className="w-4 h-4 mr-1" />
                          {team.members.length} membres
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-medium text-eg-gris mb-2">MEMBRES</p>
                        <div className="flex items-center space-x-1">
                          {getTeamMembers(team.members).slice(0, 4).map((member) => (
                            <div
                              key={member.id}
                              className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-eg-gris"
                              title={member.name}
                            >
                              {member.name.charAt(0)}
                            </div>
                          ))}
                          {team.members.length > 4 && (
                            <div className="w-8 h-8 rounded-full bg-eg-voile flex items-center justify-center text-xs text-eg-gris">
                              +{team.members.length - 4}
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-medium text-eg-gris mb-2">PROJETS ASSOCIÉS</p>
                        <div className="space-y-1">
                          {getTeamProjects(team.id).slice(0, 2).map((project) => (
                            <div key={project.id} className="flex items-center space-x-2">
                              <div
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: project.color }}
                              ></div>
                              <span className="text-xs text-eg-gris">{project.name}</span>
                            </div>
                          ))}
                          {getTeamProjects(team.id).length > 2 && (
                            <p className="text-xs text-eg-estompe">
                              +{getTeamProjects(team.id).length - 2} autres projets
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center text-xs text-eg-estompe">
                        <Calendar className="w-3 h-3 mr-1" />
                        Créée le {new Date(team.createdAt).toLocaleDateString()}
                      </div>

                      <div className="flex space-x-2 pt-3 border-t border-eg-bordure">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingTeam(team);
                            setShowEditTeamModal(true);
                          }}
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Modifier
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteTeam(team.id)}
                          className="text-eg-accent hover:text-red-700"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Supprimer
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>

            {filteredTeams.length === 0 && !teamsLoading && (
              <Card className="text-center py-12">
                <div className="w-12 h-12 bg-eg-voile rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-6 h-6 text-eg-estompe" />
                </div>
                <h3 className="text-lg font-medium text-eg-encre mb-2">Aucune équipe trouvée</h3>
                <p className="text-eg-gris mb-4">Commencez par créer votre première équipe</p>
                <Button onClick={() => setShowNewTeamModal(true)}>
                  Créer une équipe
                </Button>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Modals Projets */}
      <Modal
        isOpen={showNewProjectModal}
        onClose={() => setShowNewProjectModal(false)}
        title="Nouveau Projet"
        size="md"
      >
        <ProjectForm
          onSubmit={handleNewProject}
          onCancel={() => setShowNewProjectModal(false)}
          availableUsers={users}
        />
      </Modal>

      <Modal
        isOpen={showEditProjectModal}
        onClose={() => {
          setShowEditProjectModal(false);
          setEditingProject(null);
        }}
        title="Modifier le Projet"
        size="md"
      >
        <ProjectForm
          onSubmit={handleEditProject}
          onCancel={() => {
            setShowEditProjectModal(false);
            setEditingProject(null);
          }}
          initialData={editingProject}
          availableUsers={users}
        />
      </Modal>

      {/* Modals Équipes */}
      <Modal
        isOpen={showNewTeamModal}
        onClose={() => setShowNewTeamModal(false)}
        title="Nouvelle Équipe"
        size="md"
      >
        <TeamForm
          onSubmit={handleNewTeam}
          onCancel={() => setShowNewTeamModal(false)}
          availableUsers={users}
        />
      </Modal>

      <Modal
        isOpen={showEditTeamModal}
        onClose={() => {
          setShowEditTeamModal(false);
          setEditingTeam(null);
        }}
        title="Modifier l'Équipe"
        size="md"
      >
        <TeamForm
          onSubmit={handleEditTeam}
          onCancel={() => {
            setShowEditTeamModal(false);
            setEditingTeam(null);
          }}
          initialData={editingTeam}
          availableUsers={users}
        />
      </Modal>

      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </DashboardLayout>
  );
};

// Composant formulaire pour les projets
interface ProjectFormProps {
  onSubmit: (data: ProjectFormData) => void;
  onCancel: () => void;
  initialData?: any;
  availableUsers: any[];
}

const ProjectForm: React.FC<ProjectFormProps> = ({ onSubmit, onCancel, initialData, availableUsers }) => {
  const [formData, setFormData] = useState<ProjectFormData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    color: initialData?.color || '#3B82F6',
    members: initialData?.members || []
  });
  const [memberSearch, setMemberSearch] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  // Filtrer les utilisateurs basé sur la recherche
  const filteredUsers = availableUsers.filter((user: any) => 
    user.name.toLowerCase().includes(memberSearch.toLowerCase()) ||
    user.email.toLowerCase().includes(memberSearch.toLowerCase())
  );

  const colors = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', 
    '#8B5CF6', '#06B6D4', '#84CC16', '#F97316'
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Nom du projet */}
      <div>
        <label className="block text-sm font-medium text-eg-brun mb-2">
          Nom du projet
        </label>
        <Input
          value={formData.name}
          onChange={(value) => setFormData(prev => ({ ...prev, name: value }))}
          placeholder="Nom du projet"
          required
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-eg-brun mb-2">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Description du projet..."
          className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          rows={3}
        />
      </div>

      {/* Couleur */}
      <div>
        <label className="block text-sm font-medium text-eg-brun mb-2">
          Couleur du projet
        </label>
        <div className="flex space-x-2">
          {colors.map((color) => (
            <button
              key={color}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, color }))}
              className={`w-8 h-8 rounded-full border-2 ${
                formData.color === color ? 'border-gray-400' : 'border-eg-bord'
              }`}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
      </div>

      {/* Membres */}
      <div>
        <label className="block text-sm font-medium text-eg-brun mb-2">
          Membres du projet
        </label>
        
        {/* Barre de recherche */}
        <div className="mb-3">
          <Input
            placeholder="Rechercher par nom ou email..."
            value={memberSearch}
            onChange={setMemberSearch}
            icon={<Search className="w-4 h-4" />}
          />
        </div>

        <div className="space-y-2 max-h-32 overflow-y-auto border border-eg-bord rounded-md p-3">
          {filteredUsers.length > 0 ? (
            filteredUsers.map((user) => (
            <label key={user.id} className="flex items-center p-2 hover:bg-eg-papier rounded">
              <input
                type="checkbox"
                checked={formData.members.includes(parseInt(user.id))}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData(prev => ({
                      ...prev,
                      members: [...prev.members, parseInt(user.id)]
                    }));
                  } else {
                    setFormData(prev => ({
                      ...prev,
                      members: prev.members.filter(id => id !== parseInt(user.id))
                    }));
                  }
                }}
                className="w-4 h-4 text-eg-accent border-[#DDE2DD] rounded focus:ring-primary-500"
              />
              <div className="ml-3 flex items-center">
                <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-eg-gris mr-2">
                  {user.name.charAt(0)}
                </div>
                <div>
                  <p className="text-sm font-medium text-eg-brun">{user.name}</p>
                  <p className="text-xs text-eg-gris">{user.email}</p>
                </div>
              </div>
            </label>
          ))
          ) : (
            <div className="text-center py-4 text-eg-gris">
              <Search className="w-6 h-6 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Aucun utilisateur trouvé</p>
              <p className="text-xs">Essayez de modifier votre recherche</p>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-eg-bord">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
        >
          Annuler
        </Button>
        <Button
          type="submit"
        >
          {initialData ? 'Modifier le projet' : 'Créer le projet'}
        </Button>
      </div>
    </form>
  );
};

export default ProjectsPage;

interface TeamFormProps {
  onSubmit: (data: TeamFormData) => void;
  onCancel: () => void;
  initialData?: any;
  availableUsers: any[];
}

const TeamForm: React.FC<TeamFormProps> = ({ onSubmit, onCancel, initialData, availableUsers }) => {
  const [formData, setFormData] = useState<TeamFormData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    members: initialData?.members || [],
  });
  const [memberSearch, setMemberSearch] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const filteredUsers = availableUsers.filter((user: any) =>
    user.name.toLowerCase().includes(memberSearch.toLowerCase()) ||
    user.email.toLowerCase().includes(memberSearch.toLowerCase())
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-eg-brun mb-2">
          Nom de l'équipe
        </label>
        <Input
          value={formData.name}
          onChange={(value) => setFormData(prev => ({ ...prev, name: value }))}
          placeholder="Nom de l'équipe"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-eg-brun mb-2">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Description de l'équipe..."
          className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          rows={3}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-eg-brun mb-2">
          <UserPlus className="w-4 h-4 inline mr-1" />
          Membres de l'équipe
        </label>

        <div className="mb-3">
          <Input
            placeholder="Rechercher par nom ou email..."
            value={memberSearch}
            onChange={setMemberSearch}
            icon={<Search className="w-4 h-4" />}
          />
        </div>

        <div className="space-y-2 max-h-48 overflow-y-auto border border-eg-bord rounded-md p-3">
          {filteredUsers.length > 0 ? (
            filteredUsers.map((user: any) => {
              const userId = parseInt(user.id);
              const isChecked = formData.members.includes(userId);

              return (
                <label key={user.id} className="flex items-center p-2 hover:bg-eg-papier rounded">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData(prev => ({
                          ...prev,
                          members: [...prev.members, userId],
                        }));
                      } else {
                        setFormData(prev => ({
                          ...prev,
                          members: prev.members.filter(id => id !== userId),
                        }));
                      }
                    }}
                    className="w-4 h-4 text-eg-accent border-[#DDE2DD] rounded focus:ring-primary-500"
                  />
                  <div className="ml-3 flex items-center">
                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-eg-gris mr-2">
                      {user.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-eg-brun">{user.name}</p>
                      <p className="text-xs text-eg-gris">{user.email}</p>
                    </div>
                  </div>
                </label>
              );
            })
          ) : (
            <div className="text-center py-4 text-eg-gris">
              <Search className="w-6 h-6 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Aucun utilisateur trouvé</p>
              <p className="text-xs">Essayez de modifier votre recherche</p>
            </div>
          )}
        </div>
        <p className="text-xs text-eg-gris mt-1">
          {formData.members.length} membre{formData.members.length !== 1 ? 's' : ''} sélectionné{formData.members.length !== 1 ? 's' : ''}
        </p>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-eg-bord">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
        >
          Annuler
        </Button>
        <Button type="submit">
          {initialData ? 'Modifier l\'équipe' : 'Créer l\'équipe'}
        </Button>
      </div>
    </form>
  );
};
